/**
 * Tile physics & micro-animation helpers (Web Animations API).
 *
 * Gives placements a physical feel: an arced flight from the source seat,
 * a landing with squash-and-recover, an impact ripple of felt dust, and a
 * table shake for heavy slams. All helpers respect `prefers-reduced-motion`
 * (they resolve immediately, no animation) and are safe on detached nodes.
 */

export interface Point {
  x: number;
  y: number;
}

export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Fly `el` from `from` to its resting position (current layout position).
 * `arc` lifts the tile mid-flight, `spin` adds travel rotation. The element
 * is expected to be absolutely positioned at its FINAL spot already; the
 * animation plays in a transform and cleans itself up.
 */
export function flyTile(
  el: HTMLElement,
  from: Point,
  opts: { durationMs?: number; arc?: number; spinDeg?: number; scaleFrom?: number } = {},
): Promise<void> {
  const reduced = prefersReducedMotion();
  const dx = from.x;
  const dy = from.y;
  if (reduced || (Math.abs(dx) < 1 && Math.abs(dy) < 1)) return Promise.resolve();

  const duration = opts.durationMs ?? 340;
  const arc = opts.arc ?? -34;
  const spin = opts.spinDeg ?? 0;
  const scaleFrom = opts.scaleFrom ?? 1.12;

  const anim = el.animate(
    [
      { transform: `translate(${dx}px, ${dy}px) rotate(${spin}deg) scale(${scaleFrom})`, offset: 0 },
      { transform: `translate(${dx * 0.45}px, ${dy * 0.45 + arc}px) rotate(${spin * 0.4}deg) scale(${1 + (scaleFrom - 1) * 0.5})`, offset: 0.55 },
      { transform: "translate(0px, 2px) rotate(0deg)", offset: 0.82 },
      { transform: "translate(0px, -3px) rotate(0deg)", offset: 0.9 },
      { transform: "translate(0px, 0px) rotate(0deg)", offset: 1 },
    ],
    { duration, easing: "cubic-bezier(0.3, 0.7, 0.4, 1)", fill: "both" },
  );
  return anim.finished.then(
    () => anim.cancel(),
    () => undefined,
  );
}

/**
 * Landing squash: a quick press into the felt and recover, like bone
 * settling on wood. Plays on the tile face inside its wrapper.
 */
export function landBounce(el: HTMLElement, heavy = false): void {
  if (prefersReducedMotion()) return;
  const press = heavy ? 0.86 : 0.93;
  el.animate(
    [
      { transform: "scale(1, 1)", offset: 0 },
      { transform: `scale(${heavy ? 1.08 : 1.04}, ${press})`, offset: 0.35 },
      { transform: "scale(0.98, 1.03)", offset: 0.7 },
      { transform: "scale(1, 1)", offset: 1 },
    ],
    { duration: heavy ? 260 : 180, easing: "ease-out" },
  );
}

/**
 * Impact ripple — a brass dust ring that blooms at the landing point.
 * Appends a temporary element to `layer` (positioned ancestor).
 */
export function impactRipple(layer: HTMLElement, at: Point, heavy = false): void {
  if (prefersReducedMotion()) return;
  const ring = document.createElement("div");
  ring.className = "impact-ripple";
  const size = heavy ? 64 : 44;
  ring.style.width = `${size}px`;
  ring.style.height = `${size * 0.55}px`;
  ring.style.left = `${at.x}px`;
  ring.style.top = `${at.y}px`;
  layer.appendChild(ring);
  const anim = ring.animate(
    [
      { transform: "translate(-50%, -50%) scale(0.25)", opacity: 0.85 },
      { transform: "translate(-50%, -50%) scale(1.6)", opacity: 0 },
    ],
    { duration: heavy ? 520 : 380, easing: "cubic-bezier(0.2, 0.6, 0.4, 1)" },
  );
  anim.finished.then(
    () => ring.remove(),
    () => ring.remove(),
  );
}

/** Quick table shake when a heavy double is slammed. */
export function tableShake(el: HTMLElement, strength = 1): void {
  if (prefersReducedMotion()) return;
  el.animate(
    [
      { transform: "translate(0px, 0px)" },
      { transform: `translate(${-1.5 * strength}px, ${1 * strength}px)` },
      { transform: `translate(${1.6 * strength}px, ${-0.8 * strength}px)` },
      { transform: `translate(${-0.9 * strength}px, ${0.5 * strength}px)` },
      { transform: "translate(0px, 0px)" },
    ],
    { duration: 220, easing: "ease-out" },
  );
}

/** Deal flick: quick scale+fade pop used when tiles leave the shuffle pile. */
export function dealPop(el: HTMLElement, delayMs = 0): void {
  if (prefersReducedMotion()) return;
  el.animate(
    [
      { transform: "scale(0.4) rotate(-14deg)", opacity: 0 },
      { transform: "scale(1) rotate(0deg)", opacity: 1 },
    ],
    { duration: 240, delay: delayMs, easing: "cubic-bezier(0.2, 0.8, 0.3, 1.1)", fill: "backwards" },
  );
}

/** Raise-and-settle used when a hand tile becomes playable. */
export function handTileNudge(el: HTMLElement, delayMs: number): void {
  if (prefersReducedMotion()) return;
  el.animate(
    [
      { transform: "translateY(0)" },
      { transform: "translateY(-8px)", offset: 0.6 },
      { transform: "translateY(-4px)" },
    ],
    { duration: 420, delay: delayMs, easing: "cubic-bezier(0.3, 0.7, 0.4, 1)", fill: "both" },
  );
}
